import React, { useState, useEffect } from "react";
import { Users, Calendar, Clock, ArrowRight } from "lucide-react";

const WebinarHero = () => {
  const [webinarDateTime, setWebinarDateTime] = useState({
    date: "",
    time: "",
    timezone: "",
  });

  useEffect(() => {
    // Function to get the next upcoming webinar date and time
    const getNextWebinarDateTime = () => {
      try {
        // const now = new Date();

        // Get next upcoming date (for demo purposes, let's use next week or a specific upcoming date)
        // You can modify this logic based on your actual webinar schedule
        const webinarDate = new Date("2026-01-16T19:00:00-06:00"); // January 16, 2026 at 7:00 PM CST

        // Format date for Central Time
        const centralDateOptions = {
          weekday: "long",
          year: "numeric",
          month: "long",
          day: "numeric",
          timeZone: "America/Chicago",
        };

        const centralTimeOptions = {
          hour: "numeric",
          minute: "2-digit",
          timeZone: "America/Chicago",
          timeZoneName: "short",
        };

        const formattedDate = webinarDate.toLocaleDateString(
          "en-US",
          centralDateOptions
        );
        const centralTime = webinarDate.toLocaleTimeString(
          "en-US",
          centralTimeOptions
        );

        // Detect user's timezone and show local time if different
        let displayTime = centralTime;
        try {
          const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

          // List of Central timezone identifiers
          const centralTimezones = [
            "America/Chicago",
            "America/Indiana/Knox",
            "America/Indiana/Tell_City",
            "America/Menominee",
            "America/North_Dakota/Beulah",
            "America/North_Dakota/Center",
            "America/North_Dakota/New_Salem",
          ];

          if (!centralTimezones.includes(userTimeZone)) {
            const userTimeOptions = {
              hour: "numeric",
              minute: "2-digit",
              timeZone: userTimeZone,
              timeZoneName: "short",
            };
            const userTime = webinarDate.toLocaleTimeString(
              "en-US",
              userTimeOptions
            );
            displayTime = `${centralTime} / ${userTime}`;
          }
        } catch (error) {
          console.log(
            "Timezone detection not available, showing Central time only"
          );
        }

        return {
          date: formattedDate,
          time: displayTime,
          timezone: "CT",
        };
      } catch (error) {
        console.error("Error generating webinar date/time:", error);
        // Fallback to static date if dynamic generation fails
        return {
          date: "Friday, January 16, 2026",
          time: "7:00 PM CST",
          timezone: "CT",
        };
      }
    };

    // Set the date/time immediately
    setWebinarDateTime(getNextWebinarDateTime());
  }, []);

  const scrollToRegistration = () => {
    const registration = document.getElementById("registration");
    if (registration) {
      registration.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-gray-900 to-purple-900 py-12 sm:py-20 lg:py-32 w-full max-w-full">
      <div className="absolute inset-0 bg-black/20"></div>
      <div className="container relative z-10 mx-auto px-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 items-center">
          <div className="text-white mobile-hero-content">
            <div className="mb-4 sm:mb-6">
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs sm:text-sm font-medium bg-white/10 text-white backdrop-blur-sm">
                <Users className="w-3 h-3 sm:w-4 sm:h-4 mr-2" />
                <span className="hidden sm:inline">
                  Limited Seats Available
                </span>
                <span className="sm:hidden">Limited Seats</span>
              </span>
            </div>

            <h1 className="text-2xl sm:text-3xl lg:text-6xl font-bold mb-4 sm:mb-6 leading-tight">
              <span className="block sm:inline">Invest in Yourself</span>
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-white to-purple-200">
                Through Real Estate
              </span>
            </h1>

            <p className="text-base sm:text-lg lg:text-2xl mb-6 sm:mb-8 text-white/90 leading-relaxed">
              Join industry experts as they reveal proven strategies that have
              helped clients generate over $2M in real estate profits.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-6 sm:mb-8">
              <div className="flex items-center text-white/90 text-sm sm:text-base bg-white/10 px-3 py-2 rounded-lg backdrop-blur-sm">
                <Calendar className="w-4 h-4 sm:w-5 sm:h-5 mr-2 text-purple-300" />
                <span className="font-medium">
                  {webinarDateTime.date || (
                    <span className="animate-pulse">Loading date...</span>
                  )}
                </span>
              </div>
              <div className="flex items-center text-white/90 text-sm sm:text-base bg-white/10 px-3 py-2 rounded-lg backdrop-blur-sm">
                <Clock className="w-4 h-4 sm:w-5 sm:h-5 mr-2 text-purple-300" />
                <span className="font-medium">
                  {webinarDateTime.time || (
                    <span className="animate-pulse">Loading time...</span>
                  )}
                </span>
              </div>
            </div>

            {webinarDateTime.time && webinarDateTime.time.includes("/") && (
              <div className="text-xs text-white/70 mb-4 bg-white/5 px-3 py-1 rounded-full inline-block">
                🌍 Time shown in Eastern Time / Your Local Time
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <button
                onClick={scrollToRegistration}
                className="inline-flex items-center justify-center px-4 sm:px-8 py-3 sm:py-4 text-base sm:text-lg font-semibold text-white bg-gradient-to-r from-purple-600 to-indigo-600 rounded-lg shadow-lg hover:from-purple-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transform transition-all duration-200 hover:-translate-y-0.5">
                <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                <span className="hidden sm:inline">Reserve My FREE Seat</span>
                <span className="sm:hidden">Reserve Seat</span>
              </button>
              {/* <button className="inline-flex items-center justify-center px-4 sm:px-8 py-3 sm:py-4 text-base sm:text-lg font-semibold text-white border-2 border-white/30 rounded-lg hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-white/50 transform transition-all duration-200 hover:-translate-y-0.5">
                <Play className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                <span className="hidden sm:inline">Learn More</span>
                <span className="sm:hidden">Info</span>
              </button> */}
            </div>

            <p className="text-sm text-white/70 mt-6">
              🔒 100% Free • No Credit Card Required • Instant Access
            </p>
          </div>

          <div className="relative max-w-full overflow-visible mt-8 lg:mt-0 flex items-center justify-center mb-12 sm:mb-8 lg:mb-0">
            <div className="relative w-full max-w-md mx-auto">
              {/* Animated Real Estate Vector */}
              <div className="real-estate-animation">
                <svg
                  viewBox="0 0 400 300"
                  className="w-full h-auto animate-pulse"
                  style={{
                    filter: "drop-shadow(0 20px 40px rgba(139, 69, 193, 0.3))",
                  }}>
                  {/* Background Circle */}
                  <circle
                    cx="200"
                    cy="150"
                    r="140"
                    fill="url(#backgroundGradient)"
                    className="animate-slow-spin"
                  />

                  {/* House Structure */}
                  <g className="house-group">
                    {/* House Base */}
                    <rect
                      x="120"
                      y="180"
                      width="160"
                      height="100"
                      fill="url(#houseGradient)"
                      rx="8"
                      className="animate-float"
                    />

                    {/* Roof */}
                    <polygon
                      points="110,180 200,120 290,180"
                      fill="url(#roofGradient)"
                      className="animate-float-delayed"
                    />

                    {/* Door */}
                    <rect
                      x="185"
                      y="220"
                      width="30"
                      height="60"
                      fill="#8B5CF6"
                      rx="15"
                      className="animate-glow"
                    />

                    {/* Windows */}
                    <rect
                      x="140"
                      y="200"
                      width="25"
                      height="25"
                      fill="#F59E0B"
                      rx="4"
                      className="animate-twinkle"
                    />
                    <rect
                      x="235"
                      y="200"
                      width="25"
                      height="25"
                      fill="#F59E0B"
                      rx="4"
                      className="animate-twinkle-delayed"
                    />

                    {/* Chimney */}
                    <rect
                      x="250"
                      y="130"
                      width="15"
                      height="35"
                      fill="#6B7280"
                      rx="2"
                    />
                  </g>

                  {/* Floating Elements */}
                  <g className="floating-elements">
                    <circle
                      cx="100"
                      cy="80"
                      r="3"
                      fill="#F59E0B"
                      className="animate-bounce-slow"
                    />
                    <circle
                      cx="320"
                      cy="100"
                      r="2"
                      fill="#8B5CF6"
                      className="animate-bounce-slower"
                    />
                    <circle
                      cx="80"
                      cy="200"
                      r="2.5"
                      fill="#10B981"
                      className="animate-bounce-fast"
                    />
                  </g>

                  {/* Gradients */}
                  <defs>
                    <linearGradient
                      id="backgroundGradient"
                      x1="0%"
                      y1="0%"
                      x2="100%"
                      y2="100%">
                      <stop offset="0%" stopColor="#8B5CF6" stopOpacity="0.1" />
                      <stop
                        offset="100%"
                        stopColor="#A855F7"
                        stopOpacity="0.2"
                      />
                    </linearGradient>

                    <linearGradient
                      id="houseGradient"
                      x1="0%"
                      y1="0%"
                      x2="100%"
                      y2="100%">
                      <stop offset="0%" stopColor="#F3F4F6" />
                      <stop offset="100%" stopColor="#E5E7EB" />
                    </linearGradient>

                    <linearGradient
                      id="roofGradient"
                      x1="0%"
                      y1="0%"
                      x2="100%"
                      y2="100%">
                      <stop offset="0%" stopColor="#DC2626" />
                      <stop offset="100%" stopColor="#B91C1C" />
                    </linearGradient>
                  </defs>
                </svg>
              </div>

              {/* Professional Text Overlay */}
              <div className="absolute -bottom-8 sm:-bottom-6 md:-bottom-4 left-1/2 transform -translate-x-1/2 text-center z-10">
                <div className="bg-white/95 backdrop-blur-sm rounded-lg px-3 py-2 sm:px-4 sm:py-2 shadow-lg border border-purple-200">
                  <p className="text-purple-600 font-semibold text-xs sm:text-sm">
                    Professional Real Estate Services
                  </p>
                </div>
              </div>
            </div>

            <style jsx>{`
              @keyframes slow-spin {
                from {
                  transform: rotate(0deg);
                }
                to {
                  transform: rotate(360deg);
                }
              }

              @keyframes float {
                0%,
                100% {
                  transform: translateY(0px);
                }
                50% {
                  transform: translateY(-10px);
                }
              }

              @keyframes float-delayed {
                0%,
                100% {
                  transform: translateY(0px);
                }
                50% {
                  transform: translateY(-8px);
                }
              }

              @keyframes glow {
                0%,
                100% {
                  opacity: 1;
                }
                50% {
                  opacity: 0.7;
                }
              }

              @keyframes twinkle {
                0%,
                100% {
                  opacity: 1;
                  transform: scale(1);
                }
                50% {
                  opacity: 0.5;
                  transform: scale(1.1);
                }
              }

              @keyframes twinkle-delayed {
                0%,
                100% {
                  opacity: 1;
                  transform: scale(1);
                }
                25% {
                  opacity: 0.5;
                  transform: scale(1.1);
                }
                75% {
                  opacity: 0.8;
                  transform: scale(1.05);
                }
              }

              @keyframes bounce-slow {
                0%,
                100% {
                  transform: translateY(0px);
                }
                50% {
                  transform: translateY(-15px);
                }
              }

              @keyframes bounce-slower {
                0%,
                100% {
                  transform: translateY(0px);
                }
                50% {
                  transform: translateY(-8px);
                }
              }

              @keyframes bounce-fast {
                0%,
                100% {
                  transform: translateY(0px);
                }
                50% {
                  transform: translateY(-12px);
                }
              }

              .animate-slow-spin {
                animation: slow-spin 20s linear infinite;
              }

              .animate-float {
                animation: float 3s ease-in-out infinite;
              }

              .animate-float-delayed {
                animation: float-delayed 3s ease-in-out infinite 0.5s;
              }

              .animate-glow {
                animation: glow 2s ease-in-out infinite;
              }

              .animate-twinkle {
                animation: twinkle 2s ease-in-out infinite;
              }

              .animate-twinkle-delayed {
                animation: twinkle-delayed 2s ease-in-out infinite 0.8s;
              }

              .animate-bounce-slow {
                animation: bounce-slow 4s ease-in-out infinite;
              }

              .animate-bounce-slower {
                animation: bounce-slower 5s ease-in-out infinite 1s;
              }

              .animate-bounce-fast {
                animation: bounce-fast 3s ease-in-out infinite 2s;
              }

              @media (max-width: 768px) {
                .real-estate-animation {
                  max-width: 280px;
                  margin: 0 auto 2rem auto;
                }

                .mobile-hero-content {
                  margin-bottom: 2rem;
                }
              }
            `}</style>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WebinarHero;
